// @ts-nocheck
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import Context from '../../context/Context';
import { fetchForm, fetchFormElements } from '../../context/forms/FormsAction';
import { useNavigate, useParams } from 'react-router-dom';
import { resolveMediaUrl, URL } from "../../service/Service";
import Element from "../../components/clientFormElements/Element"
import PriceElement from "../../components/clientFormElements/PriceElement"
import Spinner from '../../components/Spinner';
import euforms from "../../images/euforms.png"
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import { CreditCardIcon, DocumentTextIcon, ShoppingBagIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import CheckoutArea from '../../components/CheckoutArea';
import HTMLRenderer from '../../components/HTMLRenderer';
import Donation from "../../components/formElements/Donation";
import { getThemeStyle } from '../../config/formThemes';
import { useI18n } from '../../i18n/I18nContext';
import { PUBLIC_LANGUAGE_SESSION_KEY, normalizeLanguage } from '../../i18n/translations';
import PaymentMethodIcon from '../../components/PaymentMethodIcon';


const ValidationToast = ({ messages = [] }) => {
    if (!messages.length) return null;

    return (
        <div
            className="pointer-events-none fixed inset-x-4 bottom-4 z-[9999] flex justify-center sm:inset-x-auto sm:right-6 sm:justify-end"
            role="alert"
            aria-live="polite"
        >
            <div className="pointer-events-auto w-full max-w-md overflow-hidden rounded-2xl border border-amber-200 bg-white shadow-2xl">
                <div className="h-1.5 bg-amber-400" />

                <div className="flex gap-4 p-5">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
                        <ExclamationTriangleIcon className="h-6 w-6" />
                    </div>

                    <div className="min-w-0 flex-1">
                        <h3 className="text-sm font-bold text-slate-950">
                            Verifica os dados do formulário
                        </h3>

                        <div className="mt-2 space-y-1.5">
                            {messages.map((message, index) => (
                                <div key={index} className="flex items-start gap-2 text-sm text-slate-600">
                                    <span className="mt-2 h-1.5 w-1.5 rounded-full bg-amber-500" />
                                    <span>{message}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const DemoModeBanner = ({ position, t }) => (
    <div
        className="relative z-[100] w-full border-y border-amber-900 bg-amber-400 px-4 py-3 text-slate-950 shadow-md"
        role="status"
        aria-live="polite"
        data-demo-mode-banner={position}
    >
        <div className="mx-auto flex max-w-7xl items-center justify-center gap-3 text-center">
            <ExclamationTriangleIcon className="h-6 w-6 shrink-0" aria-hidden="true" />
            <div>
                {position === 'header' ? (
                    <>
                        <strong className="block text-sm font-extrabold uppercase tracking-wide sm:inline sm:text-base">
                            {t('Formulário em modo demonstrativo')}
                        </strong>
                        <span className="mt-0.5 block text-xs font-semibold sm:ml-2 sm:inline sm:text-sm">
                            {t('Os pagamentos efetuados neste formulário são apenas para testes e não são pagamentos reais.')}
                        </span>
                    </>
                ) : (
                    <strong className="text-xs font-extrabold uppercase tracking-wide sm:text-sm">
                        {t('Ambiente de testes — este formulário é apenas demonstrativo.')}
                    </strong>
                )}
            </div>
        </div>
    </div>
);

const parseMoney = (value) => {
    const parsed = Number.parseFloat(String(value ?? '').replace(',', '.'));
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
};

const getElementPrice = (element) => {
    const storedCost = parseMoney(element?.cost);
    return storedCost > 0 ? storedCost : parseMoney(element?.element_value);
};

const getDonationSuggestion = (element) => {
    const storedDonation = parseMoney(element?.donation);
    return storedDonation > 0 ? storedDonation : parseMoney(element?.element_value);
};

const isPublicElementConfigured = (element) => {
    if (!element?.element_type) return false;
    if (element.element_type === 'divider') return true;
    return Boolean(String(element.label ?? '').trim());
};

const FormReader = () => {
    const { idform } = useParams()
    const draftKey = `euforms-public-draft:${idform}`;
    const draftRef = useRef(undefined);
    if (draftRef.current === undefined) {
        try {
            const storedDraft = JSON.parse(sessionStorage.getItem(draftKey) || '{}');
            const navigationEntry = window.performance?.getEntriesByType?.('navigation')?.[0];
            const refreshed = navigationEntry?.type === 'reload';
            draftRef.current = refreshed
                ? { ...storedDraft, quantities: {}, donations: {}, financialValuesReset: true }
                : storedDraft;
        }
        catch (_error) { draftRef.current = {}; }
    }
    const initialDraft = draftRef.current;
    const navigate = useNavigate();
    const { state, dispatch } = useContext(Context);
    const { setLanguage, t } = useI18n();
    const { loading, data } = state.formelements;
    const formLoading = state.form.loading;
    const formError = state.form.error;
    const formElementsError = state.formelements.error;
    const page_label = state.form.data.name;
    const info = state.form.data.info;
    const [presignedLogoUrl, setPresignedLogoUrl] = useState("");

    const paymentOptions = state.form.data.paymentOptions;
    const latitude = state.form.data.latitude;
    const longitude = state.form.data.longitude;
    const locationUrl = state.form.data.locationUrl;
    const [formElements, setFormElements] = useState([]);
    const [activePage, setActivePage] = useState(Number(initialDraft.activePage) || 1);
    const [totalPrice, setTotalPrice] = useState(0);
    const [selectedOption, setSelectedOption] = useState('');
    const [areRequiredFieldsFilled, setAreRequiredFieldsFilled] = useState(false);
    const mapRef = useRef(null);
    const [fixedValue, setFixedValue] = useState(0);
    const [itemPurchases, setItemPurchases] = useState({});
    const [regexMessages, setRegexMessages] = useState([]);
    const [answers, setAnswers] = useState(initialDraft.answers || {});
    const [maxQuantityOption, setMaxQuantityOption] = useState(state.form.data.maxQuantityOption);
    const [maxQuantityPerElement, setMaxQuantityPerElement] = useState(state.form.data.maxQuantityPerElement);
    const MAX_LIMIT = 9999;
    const [formStock, setStock] = useState(state.form.data.stock);
    const [stockOption, setStockOption] = useState(state.form.data.stockOption);
    const [saveReplyIp, setSaveReplyIp] = useState(state.form.data.saveReplyIp);
    const [ipMessage, setIpMessage] = useState(state.form.data.ipMessage);
    const [clientPublicIp, setClientPublicIp] = useState('');
    const [clientIpStatus, setClientIpStatus] = useState('idle');
    const [email, setEmail] = useState(initialDraft.email || '');
    const [isSubmittingFreeForm, setIsSubmittingFreeForm] = useState(false);
    const [freeFormError, setFreeFormError] = useState('');
    const selectedTheme = state.form.data.theme ?? 'EUPAGO';
    const isDemoMode = state.form.data.demoMode === true;
    const themeStyle = getThemeStyle(
        selectedTheme,
        state.form.data.customThemeColor,
        state.form.data.customThemeBase,
    );

    // Determine if the 1/3 section is empty
    const hasLocation = Boolean(latitude && longitude) || Boolean(String(locationUrl ?? '').trim());
    const hasInfo = Boolean(String(info ?? '').trim());
    const isOneThirdSectionEmpty = !presignedLogoUrl && !hasLocation && !hasInfo;
    // Adjust the layout dynamically

    //  const [tempMaxQuantityOfElement, setTempMaxQuantityOfElement] = useState(0);


    const [totalQuantity, setTotalQuantity] = useState(0);
    const [warningCheckoutMessage, setWarningCheckoutMessage] = useState('');
    const [canProceedToCheckout, setCanProceedToCheckout] = useState(false);
    const [donations, setDonations] = useState(initialDraft.donations || {});
    const hasPricedItems = formElements.some((element) => element.element_type === 'price');
    const hasSelectedPrice = formElements.some(
        (element) => element.element_type === 'price' && Number(element.number || element.quantity || 0) > 0,
    );
    const hasDonation = Object.values(donations).some((amount) => parseMoney(amount) >= 0.05);
    const hasSelectedItem = hasSelectedPrice || hasDonation || !hasPricedItems;
    const hasValidPaymentTotal = totalPrice >= 0.05;
    const pageCount = Math.max(1, ...Object.values(data || {}).map((element) => Number(element.pageNumber) || 1));
    const paymentElement = Object.values(data || {}).find((element) => element.element_type === 'payment');
    const paymentPage = paymentElement ? (Number(paymentElement.pageNumber) || 1) : pageCount;



    const options = [];
    for (let i = 0; i <= 10; i++) {
        options.push(<option key={i} value={i}>{i}</option>);
    }


    // const paymentImages = {
    //     "Cartão de Crédito": "https://eupago.pt/images/tarifario-meios-12.webp",
    //     "Multibanco": "https://eupago.pt/images/tarifario-meios-08.webp",
    //     "Mb Way": "https://eupago.pt/images/tarifario-meios-01.webp",
    //     "Edenred": "https://eupago.pt/images/tarifario-meios-02.webp",
    //     "Paysafe Card": "https://eupago.pt/images/tarifario-meios-03.webp",
    //     "Paysafe Cash": "https://eupago.pt/images/tarifario-meios-04.webp",
    //     "Débitos Diretos": "https://eupago.pt/images/tarifario-meios-05.webp",
    //     "Payshop": "https://eupago.pt/images/tarifario-meios-06.webp",
    //     "Przelewy24": "https://eupago.pt/images/tarifario-meios-07.webp",
    //     "Cofidis Pay": "https://eupago.pt/images/tarifario-meios-11.webp",
    //     "Santander Consumer": "https://eupago.pt/images/tarifario-meios-13.webp",
    //     "Bizum": "https://eupago.pt/images/tarifario-meios-14.webp",
    // }

    function getRegexString(regexType) {
        switch (regexType) {
            case "NUMBER":
                return /^\d+$/;
            case "TEXT":
                return /^[a-zA-Z0-9çáàâãäéèêëíìîïóòôõöúùûüñÇÁÀÂÃÄÉÈÊËÍÌÎÏÓÒÔÕÖÚÙÛÜÑ\s.,'~`´"\-!@#ªº$«»%^&*()|_+=<>?/]*$/;
            case "NIF":
                return /^[a-zA-Z0-9]{1,20}$/;
            case "NIFPT":
                return /^\d{9}$/;
            case "EMAIL":
                return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
            case "PHONE":
                return /^(?:\+|00)[1-9][0-9]{0,3}[1-9][0-9]{7,9}$/;
            case "URL":
                return /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;
            case "EMPTY":
                return /.*/;
            default:
                return /.*/;
        }
    }


    const handleInputChange = (label, value, formElementId, metadata = {}) => {
        const answerKey = formElementId || label;
        setAnswers((prevState) => ({
            ...prevState,
            [answerKey]: {
                label,
                value: metadata.serializedValue ?? value,
                displayValue: value,
                formElementId,
            },
        }));

        setFormElements((prevState) => {
            const updatedFormElements = prevState.map((element) => {
                const isChangedElement = formElementId
                    ? element.id === formElementId
                    : element.label === label;
                if (isChangedElement) {
                    if (element.element_type === 'price') {
                        return { ...element, number: value, element_value: value };
                    } else if (element.element_type === 'checkbox') {
                        return { ...element, element_value: value, checked: value };
                    } else {
                        return { ...element, element_value: value };
                    }
                }
                return element;
            });

            const areAllRequiredFieldsFilled = updatedFormElements.every(
                (element) => !element.required || element.element_value !== ""
            );

            setAreRequiredFieldsFilled(areAllRequiredFieldsFilled);

            const element = updatedFormElements.find(el => formElementId ? el.id === formElementId : el.label === label);
            if (element && (element.element_type === 'input')) {
                const regexPattern = getRegexString(element.regex);

                // If the input doesn't match the regex and it's not empty
                if (!regexPattern.test(value) || value === "") {
                    setRegexMessages((prevMessages) => {
                        const message = `Preencha o campo ${element.label} corretamente`;
                        // Avoid duplicate messages
                        return prevMessages.includes(message) ? prevMessages : [...prevMessages, message];
                    });
                } else {
                    // If the input is valid, remove any existing error message for this element
                    setRegexMessages((prevMessages) => prevMessages.filter(msg => msg !== `Preencha o campo ${element.label} corretamente`));
                }
            }

            return updatedFormElements;
        });
    };

    useEffect(() => {
        setSaveReplyIp(state.form.data.saveReplyIp);
        setIpMessage(state.form.data.ipMessage);
    }, [state.form.data.saveReplyIp, state.form.data.ipMessage]);

    useEffect(() => {
        if (saveReplyIp === undefined || saveReplyIp === null) {
            setClientPublicIp('');
            setClientIpStatus('idle');
            return;
        }

        if (saveReplyIp !== true) {
            setClientPublicIp('');
            setClientIpStatus('ready');
            return;
        }

        let cancelled = false;
        const controller = new AbortController();
        const timeoutId = window.setTimeout(() => controller.abort(), 8000);

        setClientPublicIp('');
        setClientIpStatus('loading');

        const loadPublicIp = async () => {
            try {
                const response = await fetch('https://api64.ipify.org?format=json', {
                    headers: {
                        Accept: 'application/json',
                    },
                    cache: 'no-store',
                    signal: controller.signal,
                });

                if (!response.ok) {
                    throw new Error(`IP service returned ${response.status}`);
                }

                const payload = await response.json();
                const publicIp = String(payload?.ip ?? '').trim();

                if (!publicIp) {
                    throw new Error('IP service returned an empty address');
                }

                if (!cancelled) {
                    setClientPublicIp(publicIp);
                    setClientIpStatus('ready');
                }
            } catch (_error) {
                if (!cancelled) {
                    // Em produção, o backend continua a poder obter o IP
                    // através dos headers do reverse proxy.
                    setClientIpStatus('failed');
                }
            } finally {
                window.clearTimeout(timeoutId);
            }
        };

        void loadPublicIp();

        return () => {
            cancelled = true;
            window.clearTimeout(timeoutId);
            controller.abort();
        };
    }, [saveReplyIp]);

    const DefaultIcon = L.icon({
        iconUrl: icon,
        shadowUrl: iconShadow
    });
    L.Marker.prototype.options.icon = DefaultIcon;

    const fetchPresignedLogoUrl = useCallback(async () => {
        try {
            const response = await fetch(`${URL}/getPresignedUrl/${idform}`);
            if (response.ok) {
                const url = await response.text();
                setPresignedLogoUrl(resolveMediaUrl(url));
            } else {
                console.error("Failed to fetch presigned URL");
            }
        } catch (error) {
            console.error("Error fetching presigned URL", error);
        }
    }, [idform]);

    const resolvePublicLanguage = useCallback(async (configuredLanguage) => {
        const configured = String(configuredLanguage ?? 'AUTO').toUpperCase();
        if (['PT', 'ES', 'EN'].includes(configured)) {
            const resolved = normalizeLanguage(configured, 'EN');
            sessionStorage.setItem(PUBLIC_LANGUAGE_SESSION_KEY, resolved);
            setLanguage(resolved, false);
            return;
        }

        const browserLanguage = Array.isArray(navigator.languages) && navigator.languages.length > 0
            ? navigator.languages.join(',')
            : navigator.language || '';
        try {
            const response = await fetch(
                `${URL}/api/public/forms/${idform}/language?browserLanguage=${encodeURIComponent(browserLanguage)}`,
                { headers: { Accept: 'application/json' } },
            );
            if (response.ok) {
                const payload = await response.json();
                const resolved = normalizeLanguage(payload?.language, 'EN');
                sessionStorage.setItem(PUBLIC_LANGUAGE_SESSION_KEY, resolved);
                setLanguage(resolved, false);
                return;
            }
        } catch (_error) {
            // Use the local browser-language fallback below.
        }

        const browserPrefix = browserLanguage.trim().slice(0, 2).toUpperCase();
        const resolved = normalizeLanguage(['PT', 'ES', 'EN'].includes(browserPrefix) ? browserPrefix : 'EN', 'EN');
        sessionStorage.setItem(PUBLIC_LANGUAGE_SESSION_KEY, resolved);
        setLanguage(resolved, false);
    }, [idform, setLanguage]);

    useEffect(() => {
        const url = `${URL}/api/public/forms/${idform}/formelements`;
        const url_form = `${URL}/api/public/forms/${idform}`;
        let cancelled = false;

        const loadPublicForm = async () => {
            try {
                const loadedForm = await fetchForm(url_form, {}, dispatch);

                if (cancelled || loadedForm?.status === 'PAUSED') {
                    return;
                }

                await resolvePublicLanguage(loadedForm?.language);

                await fetchFormElements(url, {}, dispatch);

                if (!cancelled && loadedForm?.logo) {
                    await fetchPresignedLogoUrl();
                }
            } catch {
                // The request helpers already store the expected unavailable
                // response in context so the page can render its friendly state.
            }
        };

        void loadPublicForm();

        return () => {
            cancelled = true;
        };
    }, [dispatch, fetchPresignedLogoUrl, idform, resolvePublicLanguage]);

    useEffect(() => {
        if (data) {
            const configuredElements = Object.values(data)
                .filter(isPublicElementConfigured)
                .map((element) => {
                    const savedAnswer = initialDraft.answers?.[element.id];
                    const savedQuantity = initialDraft.quantities?.[element.id];
                    if (element.element_type === 'price') {
                        return { ...element, element_value: String(getElementPrice(element)), number: savedQuantity || 0, quantity: savedQuantity || 0 };
                    }
                    if (element.element_type === 'donation') {
                        return { ...element, element_value: String(getDonationSuggestion(element)) };
                    }
                    return savedAnswer ? { ...element, element_value: savedAnswer.displayValue ?? savedAnswer.value } : element;
                });
            setFormElements(configuredElements);
            if (initialDraft.financialValuesReset) {
                setDonations(Object.fromEntries(
                    configuredElements
                        .filter((element) => element.element_type === 'donation')
                        .map((element) => [
                            element.id,
                            String(element.selection).toLowerCase() === 'true' ? getDonationSuggestion(element) : 0,
                        ]),
                ));
            } else if (!Object.keys(initialDraft.donations || {}).length) {
                setDonations(Object.fromEntries(configuredElements.filter((element) => element.element_type === 'donation').map((element) => [element.id, getDonationSuggestion(element)])));
            }
        }
    }, [data]);

    useEffect(() => {
        if (!data || !Object.keys(data).length) return;
        const quantities = Object.fromEntries(formElements.filter((element) => element.element_type === 'price').map((element) => [element.id, Number(element.number || element.quantity || 0)]));
        sessionStorage.setItem(draftKey, JSON.stringify({ answers, quantities, donations, email, activePage }));
    }, [activePage, answers, data, donations, draftKey, email, formElements]);

    useEffect(() => {
        let totalValue = 0;
        const purchasedItems = {};

        // Ensure formElements is not undefined before iterating over it
        if (formElements) {
            for (const formElement of formElements) {
                if (formElement.element_type === 'price') {
                    const price = getElementPrice(formElement);
                    const quantity = parseInt(formElement.number) || 0;
                    totalValue += price * quantity;
                    purchasedItems[formElement.id] = quantity;
                }
            }
        }

        setFixedValue(totalValue);
        setItemPurchases(purchasedItems);
    }, [formElements]);

    useEffect(() => {
        if ((latitude === 0 && longitude === 0) || !latitude || !longitude || mapRef.current === null) {
            return; // Do not initialize the map if latitude and longitude are zero or invalid
        }
        const map = L.map(mapRef.current).setView([latitude, longitude], 13);

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: 'Map data &copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors',
            maxZoom: 18,
        }).addTo(map);

        L.marker([latitude, longitude]).addTo(map);

        return () => {
            map.remove();
        };

    }, [latitude, longitude]);

    useEffect(() => {
        let sum = 0;

        formElements.forEach(el => {
            if (el.element_type === 'price') {
                const price = getElementPrice(el);
                const quantity = parseInt(el.number) || 0;
                sum += price * quantity;
            }

            if (el.element_type === 'donation') {
                const donationValue = donations[el.id];
                const donation = typeof donationValue === 'string'
                    ? parseFloat(donationValue.replace(',', '.'))
                    : parseFloat(donationValue);

                if (!isNaN(donation)) {
                    sum += donation;
                }
            }
        });

        setTotalPrice(sum);
    }, [formElements, donations]);

    useEffect(() => {
        setMaxQuantityOption(state.form.data?.maxQuantityOption ?? '');
        setMaxQuantityPerElement(state.form.data?.maxQuantityPerElement ?? '');
    }, [state.form.data?.maxQuantityOption, state.form.data?.maxQuantityPerElement]);

    useEffect(() => {
        setStockOption(state.form.data?.stockOption ?? '');
        setStock(state.form.data?.stock ?? '')
    }, [state.form.data?.stockOption, state.form.data?.stock]);

    useEffect(() => {
        const areAllRequiredFieldsFilled = formElements.every((element) => {
            if (!element.required) return true;

            switch (element.element_type) {
                case 'checkbox':
                    return element.element_value === true; // Ensure checkbox is checked
                case 'price':
                    return Number(element.number || 0) > 0;
                default:
                    return element.element_value && element.element_value.trim() !== "";
            }
        });
        setAreRequiredFieldsFilled(areAllRequiredFieldsFilled);
    }, [formElements]);

    const replyFields = [];

    for (const key in answers) {
        if (answers.hasOwnProperty(key)) {
            replyFields.push({
                label: answers[key].label.toUpperCase(),
                answer: answers[key].value.toString(),
                formElementId: answers[key].formElementId
            });
        }
    }

    const isClientIpReady =
        saveReplyIp === false ||
        (saveReplyIp === true && (clientIpStatus === 'ready' || clientIpStatus === 'failed'));

    const formReplies = {
        formID: idform,
        replyFields: replyFields,
        email: email,
        ip: saveReplyIp === true ? clientPublicIp : ''
    };

    /**
     * Handle the change of the selected payment method
     *
     * @param event the payment method selection event
     */
    const handleMethodSelectChange = (event) => {

        const selectedMethod =
            String(event?.target?.value || '');

        setSelectedOption(
            selectedMethod
        );

        /*
         * Sempre que o cliente muda de método,
         * limpar o aviso anterior.
         */
        let message = '';

        /*
         * Sem método selecionado não deve abrir
         * a área de checkout.
         */
        if (!selectedMethod) {

            setWarningCheckoutMessage('');
            setCanProceedToCheckout(false);

            return;
        }

        let isAllowedToCheckout = true;

        switch (selectedMethod) {

            case 'MB WAY':

                if (totalPrice > 99999) {

                    message =
                        'O pagamento com MB WAY não é possível para valores superiores a 99.999€.';

                    isAllowedToCheckout = false;
                }

                break;

            case 'Multibanco':

                if (totalPrice > 99999) {

                    message =
                        'O pagamento com Multibanco não é possível para valores superiores a 99.999€.';

                    isAllowedToCheckout = false;
                }

                break;

            case 'Cartão de Crédito':

                if (totalPrice > 3999) {

                    message =
                        'O pagamento com Cartão de Crédito não é possível para valores superiores a 3.999€.';

                    isAllowedToCheckout = false;
                }

                break;

            case 'Google Pay':

                if (totalPrice > 99999) {

                    message =
                        'O pagamento com Google Pay não é possível para valores superiores a 99.999€.';

                    isAllowedToCheckout = false;
                }

                break;

            case 'Apple Pay':

                if (totalPrice > 99999) {

                    message =
                        'O pagamento com Apple Pay não é possível para valores superiores a 99.999€.';

                    isAllowedToCheckout = false;
                }

                break;

            case 'Numerário':

                /*
                 * Numerário não depende de um serviço
                 * externo ativo no canal.
                 */
                break;

            default:

                /*
                 * Método desconhecido:
                 * não permitir continuar.
                 */
                message =
                    'O método de pagamento selecionado não é válido.';

                isAllowedToCheckout = false;

                break;
        }

        setWarningCheckoutMessage(
            message
        );

        setCanProceedToCheckout(
            isAllowedToCheckout
        );
    };

    const resetPaymentMethod = useCallback(() => {
        setSelectedOption('');
        setCanProceedToCheckout(false);
        setWarningCheckoutMessage('');
    }, []);

    const handleDonationChange = useCallback((id, amount) => {
        const donationValue = parseMoney(amount);
        setDonations(prev => prev[id] === donationValue ? prev : { ...prev, [id]: donationValue });
        resetPaymentMethod();
    }, [resetPaymentMethod]);


    const handleSelectChange = (index, value) => {
        // Reset the proceed to checkout state to make sure the user selects a valid payment method with the current total price
        handleMethodSelectChange({ target: { value: '' } });
        resetPaymentMethod();

        setFormElements(prevState => {
            const newQuantity = parseInt(value, 10) || 0;
            const updatedFormElements = prevState.map((element, elementIndex) =>
                elementIndex === index
                    ? { ...element, number: newQuantity, quantity: newQuantity }
                    : element,
            );
            setTotalQuantity(updatedFormElements.reduce(
                (sum, element) => sum + Number(element.quantity || 0),
                0,
            ));
            return updatedFormElements;
        });
    };

    if (formError || formElementsError || state.form.data.status === 'PAUSED') {
        return (
            <div className="min-h-screen bg-slate-50">
                <nav className="flex items-center bg-green-cl p-6">
                    <img className="h-8" src={euforms} alt="Eupago" />
                </nav>
                <main className="flex min-h-[calc(100vh-80px)] items-center justify-center px-4 py-12">
                    <section className="w-full max-w-xl rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-sm sm:p-12">
                        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-amber-50 text-amber-700">
                            <ExclamationTriangleIcon className="h-9 w-9" aria-hidden="true" />
                        </div>
                        <h1 className="mt-6 text-3xl font-bold text-slate-950">Formulário indisponível</h1>
                        <p className="mt-3 text-base leading-7 text-slate-600">
                            Este formulário não está disponível de momento. Por favor, tente novamente mais tarde.
                        </p>
                    </section>
                </main>
            </div>
        );
    }

    if (loading === true || formLoading === true) {
        return (
            <Spinner></Spinner>
        )
    }

    // Function to calculate the maximum allowed quantity
    const getMaxAllowed = (formElement, formStock, totalQuantity, maxQuantityPerElement, stockOption, maxQuantityOption) => {
        if (stockOption === 'LIMITED') {
            if (maxQuantityOption === 'UNLIMITED') {
                return Math.min(
                    MAX_LIMIT,
                    formStock - totalQuantity + (formElement.quantity || 0)
                );
            } else if (maxQuantityOption === 'LIMITED') {

                let byStock = formStock - totalQuantity - maxQuantityPerElement + (formElement.quantity || 0);
                return Math.min(
                    MAX_LIMIT,
                    maxQuantityPerElement,
                    byStock > 0 ? maxQuantityPerElement - totalQuantity + (formElement.quantity || 0) : MAX_LIMIT,
                    byStock < 0 ? formStock - totalQuantity + (formElement.quantity || 0) : byStock
                );
            } else if (maxQuantityOption === 'TYPE_LIMITED') {
                return Math.min(
                    MAX_LIMIT,
                    formElement.maxQuantityPerElement,
                    formStock - totalQuantity + (formElement.quantity || 0)
                );
            }
        } else if (stockOption === 'TYPE_LIMITED') {
            if (maxQuantityOption === 'UNLIMITED') {
                return Math.min(
                    MAX_LIMIT,
                    formElement.stock
                );
            } else if (maxQuantityOption === 'LIMITED') {
                return Math.min(
                    MAX_LIMIT,
                    formElement.stock,
                    maxQuantityPerElement - totalQuantity + (formElement.quantity || 0)
                );
            } else if (maxQuantityOption === 'TYPE_LIMITED') {
                return Math.min(
                    MAX_LIMIT,
                    formElement.maxQuantityPerElement,
                    formElement.stock
                );
            }
        } else if (stockOption === 'UNLIMITED') {
            if (maxQuantityOption === 'LIMITED') {
                return Math.min(
                    MAX_LIMIT,
                    maxQuantityPerElement - totalQuantity + (formElement.quantity || 0)
                );
            }
            if (maxQuantityOption === 'TYPE_LIMITED') {
                return Math.min(
                    MAX_LIMIT,
                    formElement.maxQuantityPerElement
                );
            }
        }
        return MAX_LIMIT;
    };

    const handleEmailChange = (e) => {
        setEmail(e.target.value);
    }

    const handleFreeFormSubmit = async (event) => {
        event.preventDefault();

        if (isSubmittingFreeForm || !isClientIpReady || !areRequiredFieldsFilled || regexMessages.length > 0 || !hasSelectedItem) {
            return;
        }

        setIsSubmittingFreeForm(true);
        setFreeFormError('');

        try {
            const response = await fetch(`${URL}/api/replies`, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    ...formReplies,
                    referenceID: null,
                }),
            });

            if (!response.ok) {
                const message = await response.text();
                throw new Error(message || 'Não foi possível enviar o formulário.');
            }

            navigate('/clientforms/success/form', {
                replace: true,
                state: { formId: idform },
            });
        } catch (submitError) {
            setFreeFormError(submitError?.message || 'Não foi possível enviar o formulário. Tenta novamente.');
        } finally {
            setIsSubmittingFreeForm(false);
        }
    };


    const validationMessages = [];

    if (hasPricedItems && !hasSelectedItem) {
        validationMessages.push('Selecione pelo menos uma unidade de um artigo antes de continuar.');
    }

    if (totalPrice > 0 && !hasValidPaymentTotal) {
        validationMessages.push('O valor mínimo para pagamento é 0,05 €.');
    }

    if (totalPrice > 0 && !areRequiredFieldsFilled) {
        validationMessages.push(
            'Preencha todos os campos obrigatórios antes de continuar.'
        );
    }

    if (totalPrice > 0 && regexMessages.length > 0) {
        validationMessages.push(...regexMessages);
    }

    return (
        <div
            className="form-theme-root min-h-screen overflow-x-hidden"
            style={{
                ...themeStyle,
                background: 'var(--form-page-background)',
                color: 'var(--form-text)',
            }}
            data-form-theme={selectedTheme}
        >
            {isDemoMode && <DemoModeBanner position="header" t={t} />}
            <nav
                className="form-theme-nav border-b px-5 py-5 shadow-[0_8px_30px_rgba(8,47,43,0.12)] sm:px-8"
                style={{
                    background: 'var(--form-accent)',
                    borderColor: 'var(--form-border)',
                    color: 'var(--form-accent-text)',
                }}
            >
                <div className="mx-auto flex max-w-7xl items-center justify-between">
                    <img className="h-8 sm:h-9" src={euforms} alt="Eupago" />
                    <span
                        className="hidden rounded-full border px-4 py-2 text-xs font-semibold tracking-wide sm:inline-flex"
                        style={{
                            borderColor: 'color-mix(in srgb, var(--form-accent-text) 24%, transparent)',
                            background: 'color-mix(in srgb, var(--form-accent-text) 8%, transparent)',
                            color: 'var(--form-accent-text)',
                        }}
                    >
                        Formulário seguro
                    </span>
                </div>
            </nav>

            <main
                className="public-form-workspace relative min-h-[calc(100vh-132px)] overflow-hidden px-4 py-8 sm:px-6 sm:py-12 lg:py-16"
                style={{ background: 'var(--form-page-background)' }}
            >
                <div
                    className="pointer-events-none absolute -left-32 top-40 h-72 w-72 rounded-full blur-3xl"
                    style={{ background: 'color-mix(in srgb, var(--form-accent) 18%, transparent)' }}
                />
                <div
                    className="pointer-events-none absolute -right-32 top-10 h-96 w-96 rounded-full blur-3xl"
                    style={{ background: 'color-mix(in srgb, var(--form-soft) 62%, transparent)' }}
                />

                <div
                    className="form-theme-shell relative mx-auto max-w-7xl overflow-hidden border"
                    style={{
                        background: 'var(--form-surface)',
                        borderColor: 'var(--form-border)',
                        borderRadius: 'var(--form-radius)',
                        boxShadow: 'var(--form-shadow)',
                        color: 'var(--form-text)',
                    }}
                >
                    <div
                        className="form-theme-hero border-b px-6 py-8 sm:px-10 sm:py-10 lg:px-12"
                        style={{
                            background: 'var(--form-soft)',
                            borderColor: 'var(--form-border)',
                        }}
                    >
                        <div className="flex items-start gap-4">
                            <div
                                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl"
                                style={{
                                    background: 'var(--form-accent)',
                                    color: 'var(--form-accent-text)',
                                    border: '1px solid var(--form-border)',
                                }}
                            >
                                <DocumentTextIcon className="h-6 w-6" />
                            </div>

                            <div className="min-w-0">
                                <p
                                    className="text-xs font-bold uppercase tracking-[0.22em]"
                                    style={{ color: 'var(--form-accent)' }}
                                >
                                    Formulário
                                </p>
                                <h1
                                    data-i18n-ignore="true"
                                    className="mt-2 break-words text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-[2.75rem] lg:leading-tight"
                                    style={{ color: 'var(--form-text)' }}
                                >
                                    {page_label}
                                </h1>
                                <p
                                    className="mt-3 max-w-2xl text-sm leading-6 sm:text-base"
                                    style={{ color: 'var(--form-muted)' }}
                                >
                                    Preencha os dados abaixo e reveja o seu pedido antes de continuar.
                                </p>
                                {saveReplyIp && (
                                    <p
                                        data-i18n-ignore="true"
                                        className="mt-3 inline-flex rounded-full px-3 py-1.5 text-xs font-medium"
                                        style={{
                                            background: 'var(--form-surface)',
                                            color: 'var(--form-muted)',
                                            border: '1px solid var(--form-border)',
                                        }}
                                    >
                                        {ipMessage}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 gap-8 p-5 sm:p-8 lg:grid-cols-[minmax(0,1fr)_340px] lg:gap-10 lg:p-10 xl:grid-cols-[minmax(0,1fr)_380px] xl:p-12">
                        {!isOneThirdSectionEmpty && (
                            <aside className="order-first flex w-full flex-col gap-5 lg:order-last">
                                {presignedLogoUrl && (
                                    <div
                                        className="group overflow-hidden rounded-3xl border p-3 shadow-sm"
                                        style={{
                                            background: 'var(--form-soft)',
                                            borderColor: 'var(--form-border)',
                                        }}
                                    >
                                        <div
                                            className="flex min-h-52 items-center justify-center overflow-hidden rounded-2xl"
                                            style={{ background: 'var(--form-surface)' }}
                                        >
                                            <img
                                                src={presignedLogoUrl}
                                                alt={`Imagem de ${page_label || 'formulário'}`}
                                                className="max-h-80 w-full object-contain transition duration-500 group-hover:scale-[1.02]"
                                            />
                                        </div>
                                    </div>
                                )}

                                {hasInfo && (
                                    <div
                                        className="w-full rounded-3xl border p-5 text-sm leading-7 shadow-sm sm:p-6"
                                        style={{
                                            background: 'var(--form-surface)',
                                            borderColor: 'var(--form-border)',
                                            color: 'var(--form-text)',
                                        }}
                                    >
                                        <p
                                            className="mb-2 text-xs font-bold uppercase tracking-[0.18em]"
                                            style={{ color: 'var(--form-accent)' }}
                                        >
                                            Sobre este formulário
                                        </p>
                                        <div data-i18n-ignore="true" style={{ color: 'var(--form-muted)' }}>
                                            <HTMLRenderer html={state.form.data.info} />
                                        </div>
                                    </div>
                                )}

                                {latitude !== 0 && longitude !== 0 && (
                                    <div
                                        className="h-64 w-full overflow-hidden rounded-3xl border p-2 shadow-sm"
                                        style={{
                                            background: 'var(--form-surface)',
                                            borderColor: 'var(--form-border)',
                                        }}
                                    >
                                        <div ref={mapRef} className="h-full w-full"></div>
                                    </div>
                                )}

                                {locationUrl && (
                                    <a
                                        href={locationUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex w-full items-center justify-center rounded-2xl px-5 py-3.5 text-sm font-semibold shadow-sm transition hover:-translate-y-0.5 hover:opacity-90"
                                        style={{
                                            background: 'var(--form-accent)',
                                            color: 'var(--form-accent-text)',
                                        }}
                                    >
                                        Ver localização no Google Maps
                                    </a>
                                )}
                            </aside>
                        )}

                        <section className="min-w-0 w-full">
                            <form onSubmit={(event) => event.preventDefault()}>
                                <section
                                    className="rounded-3xl border p-5 shadow-sm sm:p-7"
                                    style={{
                                        background: 'var(--form-surface)',
                                        borderColor: 'var(--form-border)',
                                        color: 'var(--form-text)',
                                    }}
                                >
                                    <div className="mb-7 flex items-start gap-3">
                                        <div
                                            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
                                            style={{
                                                background: 'var(--form-soft)',
                                                color: 'var(--form-text)',
                                                border: '1px solid var(--form-border)',
                                            }}
                                        >
                                            <DocumentTextIcon className="h-5 w-5" />
                                        </div>
                                        <div>
                                            <h2
                                                className="text-xl font-bold tracking-tight"
                                                style={{ color: 'var(--form-text)' }}
                                            >
                                                Os seus dados
                                            </h2>
                                            <p
                                                className="mt-1 text-sm leading-6"
                                                style={{ color: 'var(--form-muted)' }}
                                            >
                                                Os campos assinalados com * são obrigatórios.
                                            </p>
                                        </div>
                                    </div>

                                    <div>
                                        {formElements.filter((element) => element.element_type !== 'payment').map((formElement, i) => (
                                            <div key={formElement.id} className={(Number(formElement.pageNumber) || 1) === activePage ? 'block' : 'hidden'} style={{ color: 'var(--form-text)' }}>
                                                <Element
                                                    i={i}
                                                    formElement={answers[formElement.id]
                                                        ? { ...formElement, element_value: answers[formElement.id].displayValue }
                                                        : formElement}
                                                    formId={idform}
                                                    onInputChange={(label, value, metadata) => handleInputChange(label, value, formElement.id, metadata)}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </section>

                                <section
                                    className="form-theme-cart mt-7 rounded-3xl border p-5 shadow-sm sm:p-7"
                                    style={{
                                        background: 'var(--form-surface)',
                                        borderColor: 'var(--form-border)',
                                        color: 'var(--form-text)',
                                    }}
                                >
                                    <div className="mb-6 flex items-center justify-between gap-4">
                                        <div className="flex items-center gap-3">
                                            <div
                                                className="flex h-10 w-10 items-center justify-center rounded-xl"
                                                style={{
                                                    background: 'var(--form-accent)',
                                                    color: 'var(--form-accent-text)',
                                                }}
                                            >
                                                <ShoppingBagIcon className="h-5 w-5" />
                                            </div>
                                            <div>
                                                <h2
                                                    className="text-xl font-bold tracking-tight"
                                                    style={{ color: 'var(--form-text)' }}
                                                >
                                                    {t('Carrinho')}
                                                </h2>
                                                <p
                                                    className="mt-1 text-sm"
                                                    style={{ color: 'var(--form-muted)' }}
                                                >
                                                    {t('Escolha os artigos e as quantidades.')}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {formElements.filter((element) => element.element_type !== 'payment').map((formElement, i) => {
                                        if (formElement.element_type !== 'price' && formElement.element_type !== 'donation') return null;

                                        return (
                                            <div
                                                key={formElement.id}
                                                className={`${(Number(formElement.pageNumber) || 1) === activePage ? '' : 'hidden'} ${formElement.element_type === 'price'
                                                    ? 'mb-3 grid w-full grid-cols-1 gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-stretch'
                                                    : 'w-full'}`}
                                            >
                                                <div style={{ color: 'var(--form-text)' }}>
                                                    <PriceElement i={i} formElement={formElement} formId={idform} />
                                                </div>

                                                {formElement.element_type === 'donation' && (
                                                    <Donation
                                                        id={formElement.id}
                                                        label={formElement.label}
                                                        element_value={getDonationSuggestion(formElement)}
                                                        fixed={Boolean(formElement.selection)}
                                                        onDonationChange={handleDonationChange}
                                                    />
                                                )}

                                                {formElement.element_type === 'price' && (
                                                    <div
                                                        className="flex min-h-16 w-full items-center justify-between rounded-2xl border p-2 shadow-sm md:w-auto"
                                                        style={{
                                                            background: 'var(--form-surface)',
                                                            borderColor: 'var(--form-border)',
                                                            color: 'var(--form-text)',
                                                        }}
                                                    >
                                                        <span
                                                            className="mr-3 text-xs font-bold uppercase tracking-[0.12em]"
                                                            style={{ color: 'var(--form-muted)' }}
                                                        >
                                                            {t('Qtd.')}
                                                        </span>
                                                        <div className="flex items-center">
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    const inputField = document.getElementById(`quantity-input-${i}`);
                                                                    const inputValue = parseInt(inputField.value, 10) || 0;
                                                                    const newValue = Math.max(0, inputValue - 1);
                                                                    inputField.value = newValue;
                                                                    handleSelectChange(i, newValue);
                                                                }}
                                                                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-lg font-bold transition hover:-translate-y-0.5 hover:opacity-90"
                                                                style={{
                                                                    background: 'var(--form-accent)',
                                                                    color: 'var(--form-accent-text)',
                                                                }}
                                                                aria-label={`Diminuir quantidade de ${formElement.label}`}
                                                            >
                                                                -
                                                            </button>

                                                            <input
                                                                id={`quantity-input-${i}`}
                                                                type="text"
                                                                onInput={e => {
                                                                    const inputValue = e.target.value;
                                                                    if (/[^0-9]/.test(inputValue) || /--/.test(inputValue)) {
                                                                        e.target.value = inputValue.replace(/[^0-9]/g, '');
                                                                        return;
                                                                    }
                                                                    const parsedValue = parseInt(e.target.value, 10);
                                                                    const maxAllowed = Math.max(0, getMaxAllowed(formElement, formStock, totalQuantity, maxQuantityPerElement, stockOption, maxQuantityOption));
                                                                    if (parsedValue > maxAllowed) {
                                                                        e.target.value = maxAllowed;
                                                                        handleSelectChange(i, maxAllowed);
                                                                    } else {
                                                                        handleSelectChange(i, parsedValue);
                                                                    }
                                                                }}
                                                                className="mx-1 h-10 w-16 border-transparent bg-transparent text-center text-lg font-bold focus:border-transparent focus:ring-transparent"
                                                                style={{
                                                                    color: 'var(--form-text)',
                                                                    caretColor: 'var(--form-accent)',
                                                                }}
                                                                placeholder="0"
                                                                onWheel={() => document.activeElement.blur()}
                                                                onBlur={e => {
                                                                    const inputValue = e.target.value;
                                                                    if (!/^\d*$/.test(inputValue)) {
                                                                        e.target.value = 0;
                                                                        handleSelectChange(i, 0);
                                                                        return;
                                                                    }
                                                                    const parsedValue = parseInt(inputValue, 10);
                                                                    const maxAllowed = Math.max(0, getMaxAllowed(formElement, formStock, totalQuantity, maxQuantityPerElement, stockOption, maxQuantityOption));
                                                                    if (parsedValue > maxAllowed) {
                                                                        e.target.value = maxAllowed;
                                                                        handleSelectChange(i, maxAllowed);
                                                                    } else if (parsedValue < 0) {
                                                                        e.target.value = 0;
                                                                        handleSelectChange(i, 0);
                                                                    }
                                                                }}
                                                                min="0"
                                                                inputMode="numeric"
                                                                aria-label={`Quantidade de ${formElement.label}`}
                                                            />

                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    const inputField = document.getElementById(`quantity-input-${i}`);
                                                                    const inputValue = parseInt(inputField.value, 10) || 0;
                                                                    const maxAllowed = Math.max(0, getMaxAllowed(formElement, formStock, totalQuantity, maxQuantityPerElement, stockOption, maxQuantityOption));
                                                                    const newValue = Math.min(maxAllowed, inputValue + 1);
                                                                    inputField.value = newValue;
                                                                    handleSelectChange(i, newValue);
                                                                }}
                                                                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-lg font-bold transition hover:-translate-y-0.5 hover:opacity-90"
                                                                style={{
                                                                    background: 'var(--form-accent)',
                                                                    color: 'var(--form-accent-text)',
                                                                }}
                                                                aria-label={`Aumentar quantidade de ${formElement.label}`}
                                                            >
                                                                +
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}

                                    <div
                                        className="mt-5 flex flex-col gap-2 rounded-2xl px-5 py-5 shadow-[0_16px_35px_rgba(8,64,58,0.18)] sm:flex-row sm:items-center sm:justify-between"
                                        style={{
                                            background: 'var(--form-accent)',
                                            color: 'var(--form-accent-text)',
                                        }}
                                    >
                                        <div>
                                            <p className="text-xs font-bold uppercase tracking-[0.18em] opacity-75">
                                                {t('Total do pedido')}
                                            </p>
                                            <p className="mt-1 text-sm opacity-80">
                                                {t('Confirme o valor antes de continuar.')}
                                            </p>
                                        </div>
                                        <p className="whitespace-nowrap text-3xl font-extrabold tracking-tight">
                                            {totalPrice.toFixed(2).replace(/\./g, ',')} €
                                        </p>
                                    </div>
                                </section>

                                {activePage === paymentPage && totalPrice <= 0 && !hasPricedItems && areRequiredFieldsFilled && regexMessages.length === 0 && (
                                    <section
                                        className="mt-7 rounded-3xl border p-5 shadow-sm sm:p-7"
                                        style={{
                                            background: 'var(--form-surface)',
                                            borderColor: 'var(--form-border)',
                                            color: 'var(--form-text)',
                                        }}
                                    >
                                        {freeFormError && (
                                            <div
                                                className="mb-4 rounded-xl border border-red-300 bg-red-50 p-4 text-sm text-red-700"
                                                role="alert"
                                            >
                                                {freeFormError}
                                            </div>
                                        )}

                                        {!isClientIpReady && saveReplyIp === true && (
                                            <div
                                                className="mb-4 rounded-xl border px-4 py-3 text-sm"
                                                style={{
                                                    color: 'var(--form-muted)',
                                                    borderColor: 'var(--form-border)',
                                                    background: 'var(--form-soft)',
                                                }}
                                            >
                                                A preparar o registo do IP…
                                            </div>
                                        )}

                                        <div className="flex justify-end">
                                            <button
                                                type="button"
                                                onClick={handleFreeFormSubmit}
                                                disabled={isSubmittingFreeForm || !isClientIpReady}
                                                className="w-full rounded-2xl px-6 py-3.5 font-semibold transition hover:-translate-y-0.5 hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
                                                style={{
                                                    background: 'var(--form-accent)',
                                                    color: 'var(--form-accent-text)',
                                                }}
                                            >
                                                {isSubmittingFreeForm
                                                    ? 'A enviar…'
                                                    : !isClientIpReady && saveReplyIp === true
                                                        ? 'A preparar…'
                                                        : 'Enviar formulário'}
                                            </button>
                                        </div>
                                    </section>
                                )}

                                {activePage === paymentPage && hasValidPaymentTotal && hasSelectedItem && (
                                    <section
                                        className="mt-7 rounded-3xl border p-5 shadow-sm sm:p-7"
                                        style={{
                                            background: 'var(--form-surface)',
                                            borderColor: 'var(--form-border)',
                                            color: 'var(--form-text)',
                                        }}
                                    >
                                        <div className="mb-6 flex items-start gap-3">
                                            <div
                                                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
                                                style={{
                                                    background: 'var(--form-soft)',
                                                    color: 'var(--form-accent)',
                                                    border: '1px solid var(--form-border)',
                                                }}
                                            >
                                                <CreditCardIcon className="h-5 w-5" />
                                            </div>
                                            <div>
                                                <h2
                                                    className="text-xl font-bold tracking-tight"
                                                    style={{ color: 'var(--form-text)' }}
                                                >
                                                    {t('Pagamento')}
                                                </h2>
                                                <p
                                                    className="mt-1 text-sm leading-6"
                                                    style={{ color: 'var(--form-muted)' }}
                                                >
                                                    {t('Selecione o método que pretende utilizar.')}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="grid gap-3 sm:grid-cols-2">
                                            {(paymentOptions || []).map((option) => {
                                                const selected = selectedOption === option;
                                                return (
                                                    <label
                                                        key={option}
                                                        htmlFor={`payment-${option.replace(/[^a-zA-Z0-9_-]/g, '-')}`}
                                                        className="flex cursor-pointer items-center gap-3 rounded-2xl border px-4 py-4 transition hover:-translate-y-0.5"
                                                        style={{
                                                            background: selected ? 'var(--form-soft)' : 'var(--form-surface)',
                                                            borderColor: selected ? 'var(--form-accent)' : 'var(--form-border)',
                                                            color: 'var(--form-text)',
                                                            boxShadow: selected
                                                                ? '0 0 0 2px color-mix(in srgb, var(--form-accent) 30%, transparent)'
                                                                : 'none',
                                                        }}
                                                    >
                                                        <input
                                                            type="radio"
                                                            id={`payment-${option.replace(/[^a-zA-Z0-9_-]/g, '-')}`}
                                                            name="paymentMethod"
                                                            value={option}
                                                            checked={selected}
                                                            onChange={handleMethodSelectChange}
                                                            className="h-5 w-5 cursor-pointer"
                                                            style={{ accentColor: 'var(--form-accent)' }}
                                                        />
                                                        <span className="flex h-9 w-14 shrink-0 items-center justify-center rounded-lg bg-white px-1.5 shadow-sm">
                                                            <PaymentMethodIcon method={option} className="max-h-7 w-auto max-w-12" />
                                                        </span>
                                                        <span className="font-semibold">{t(option)}</span>
                                                    </label>
                                                );
                                            })}
                                        </div>

                                        {warningCheckoutMessage && (
                                            <div className="mt-4 flex items-start rounded-2xl border border-red-300 bg-red-50 p-4 text-sm text-red-700">
                                                <ExclamationTriangleIcon className="mr-2 h-5 w-5 shrink-0" />
                                                {warningCheckoutMessage}
                                            </div>
                                        )}

                                        {canProceedToCheckout && !isClientIpReady && saveReplyIp === true && (
                                            <div
                                                className="mt-6 rounded-xl border px-4 py-3 text-sm"
                                                style={{
                                                    color: 'var(--form-muted)',
                                                    borderColor: 'var(--form-border)',
                                                    background: 'var(--form-soft)',
                                                }}
                                            >
                                                A preparar o registo do IP antes de continuar para o pagamento…
                                            </div>
                                        )}

                                        {canProceedToCheckout && isClientIpReady && (
                                            <div
                                                className="mt-6 border-t pt-6"
                                                style={{ borderColor: 'var(--form-border)' }}
                                            >
                                                <CheckoutArea
                                                    method={selectedOption}
                                                    value={totalPrice}
                                                    formReplies={formReplies}
                                                    fixedValue={fixedValue}
                                                    itemPurchases={itemPurchases}
                                                    donations={donations}
                                                    mbStartDate={state.form.data.mbStartDate}
                                                    mbEndDate={state.form.data.mbEndDate}
                                                    email={email}
                                                    onEmailChange={handleEmailChange}
                                                />
                                            </div>
                                        )}
                                    </section>
                                )}
                                {pageCount > 1 && (
                                    <nav className="mt-7 flex items-center justify-between rounded-2xl border p-4" style={{ borderColor: 'var(--form-border)', background: 'var(--form-surface)' }} aria-label="Navegação entre páginas">
                                        <button type="button" disabled={activePage === 1} onClick={() => setActivePage((page) => Math.max(1, page - 1))} className="rounded-xl border px-5 py-3 text-sm font-bold disabled:cursor-not-allowed disabled:opacity-40" style={{ borderColor: 'var(--form-border)', color: 'var(--form-text)' }}>Anterior</button>
                                        <span className="text-sm font-semibold" style={{ color: 'var(--form-muted)' }}>Página {activePage} de {pageCount}</span>
                                        {activePage < pageCount ? (
                                            <button type="button" onClick={() => setActivePage((page) => Math.min(pageCount, page + 1))} className="rounded-xl px-5 py-3 text-sm font-bold" style={{ background: 'var(--form-accent)', color: 'var(--form-accent-text)' }}>Seguinte</button>
                                        ) : <span className="w-24" />}
                                    </nav>
                                )}
                            </form>
                        </section>
                    </div>
                </div>
            </main>

            <ValidationToast messages={validationMessages} />

            <footer
                className="form-theme-footer flex items-center justify-center p-4 text-center"
                style={{
                    background: 'var(--form-accent)',
                    color: 'var(--form-accent-text)',
                }}
            >
                <div className="mr-6 flex flex-shrink-0 items-center">
                    <small>© Eupago - Instituição de Pagamento - {new Date().getFullYear()}</small>
                </div>
            </footer>
            {isDemoMode && <DemoModeBanner position="footer" t={t} />}
        </div>
    )
}

export default FormReader;
